/**
 * Design tokens — SerBio Health.
 *
 * Fonte: Brandbook SerBio Health v1.0 (manual de identidade da marca).
 * Substitui a paleta anterior "Apple Clean" (2026-08, rebrand de investidores)
 * — ver git history de `tokens.ts` para os valores antigos.
 *
 * Duas paletas convivem aqui, e a separação é intencional:
 *
 * - `color`: a paleta neutra do produto. ~90% da interface, por regra
 *   explícita do brandbook. A cor de ação (`accent`, o ouro SerBio) aparece
 *   só em CTA, link e ícone — nunca como preenchimento de área grande.
 * - `status`: paleta SEMÂNTICA do semáforo. Carrega informação de estado, não
 *   decoração. Só aparece em badge, dot e borda fina — nunca como bloco de fundo
 *   grande. Não conta como quebra da parcimônia de cor porque é funcional.
 *
 * ATENÇÃO: `status` é para o PAINEL DO MÉDICO. O app do paciente nunca mostra
 * verde/amarelo/vermelho — ver `toneForStatus` no app, que traduz estado em
 * linguagem não-alarmista (build-prompt §3.1).
 */

export const color = {
  /** Fundo principal e cards — --sb-surface do brandbook. */
  white: '#FFFFFF',
  /** Fundo de página e de seções alternadas — --sb-bg do brandbook. */
  surface: '#F7F4EF',
  /** Bordas e divisores — usar com parcimônia. --sb-border. */
  border: '#D8D3CC',
  /** Headlines e corpo. --sb-ink / --sb-text. */
  text: '#211F1E',
  /** Subtítulos, legendas, metadados. Nunca cor de marca. --sb-text-muted. */
  textSecondary: '#78746F',
  /** Links, CTAs e ícones. O ouro SerBio — --sb-brand. */
  accent: '#C69010',
  /** Estado pressionado/hover — --sb-brand-pressed. */
  accentHover: '#9A700C',
  black: '#000000',
} as const;

/**
 * Paleta semântica do semáforo.
 *
 * `fg` precisa passar contraste 4.5:1 sobre branco (WCAG AA, texto pequeno) —
 * é o próprio padrão que o brandbook declara para toda a plataforma. `bg`/
 * `border` são derivados por mistura com branco (~90%/~60%), o brandbook só
 * dá o hex de cada estado (verde=Estável, "amarelo"=Atenção, vermelho=Risco
 * alto), não a tinta clara/borda.
 *
 * ACHADO: o hex de "Atenção" do brandbook (#C1791F) dá só 3.49:1 contra
 * branco — reprova 4.5:1 como cor de TEXTO, embora passe os 3:1 exigidos
 * para elemento gráfico/borda/badge. `yellow.fg` por isso NÃO é o hex literal
 * do brandbook: é uma variante escurecida da mesma família (#A5650E, 4.70:1,
 * calculado por luminância relativa) — só usada onde a cor carrega texto.
 * `bg`/`border` continuam com a tinta original do brandbook, que é onde ela
 * foi pensada para aparecer.
 */
export const status = {
  green: { fg: '#5F7A52', bg: '#EFF2EE', border: '#BFCABA' },
  yellow: { fg: '#A5650E', bg: '#F9F2E9', border: '#E6C9A5' },
  red: { fg: '#B4483C', bg: '#F8EDEC', border: '#E1B6B1' },
} as const;

/**
 * Paleta CATEGÓRICA — fatias de rosca, séries nomeadas (estágios de sono e
 * afins). O brandbook não cobre esse uso (ele especifica só duas cores DE
 * DADO — verde/azul — para gráfico de tendência de até três séries); esta
 * paleta precisa de 4+1 e é retintada mantendo o espírito da marca.
 *
 * `categorical[0]` (azul/"profundo") e `categorical[2]` (verde/"leve") SÃO
 * os hex oficiais de marca — Azul Dado (`#3D5A80`) e Verde Evolução
 * (`#5F7A52`). Os outros dois (terracota, malva) são derivados: tentativas
 * de reaproveitar tons ainda mais dessaturados/mauve não passaram na
 * validação de daltonismo (roxo e azul colidem sob protanopia quando a
 * saturação cai demais — restrição física da simulação, não falta de
 * tentativa). O conjunto abaixo É mais saturado que o ideal minimalista do
 * brandbook — é a concessão necessária para continuar daltônico-seguro.
 *
 * Validada por simulação de daltonismo (protanopia, deuteranopia,
 * tritanopia) com distância CIE76 entre todos os pares — rodar de novo antes
 * de qualquer ajuste fino:
 *   node packages/ui/scripts/validar-paleta.js '#3D5A80' '#C97A3D' '#5F7A52' '#B87FAE' '#4D4947'
 * Pior par: `#3D5A80` vs `#4D4947` (o "Outros") sob tritanopia, ΔE 18,2.
 *
 * O critério de 18 não é arbitrário — é calibrado contra a Okabe-Ito, a
 * paleta de referência para daltonismo, que tem ΔE 18,1 no próprio pior par
 * (`#009E73` vs `#CC79A7` sob deuteranopia). Um limiar mais rígido que isso
 * reprovaria o padrão-ouro e não teria como estar certo.
 *
 * QUATRO cores é o teto, não preguiça: ver histórico deste arquivo para o
 * porquê de uma quinta categoria não caber com segurança. Por isso quem
 * consome agrupa da quinta em diante.
 *
 * IMPORTANTE: dois arquivos fora de `packages/ui` duplicam estes hex como
 * literais em vez de importar — `apps/doctor/src/screens/OverviewCards.tsx`
 * e `apps/patient/src/screens/Dashboard.tsx` (mapa de cor dos estágios de
 * sono). Mudou aqui, muda lá também.
 */
export const categorical = ['#3D5A80', '#C97A3D', '#5F7A52', '#B87FAE'] as const;

/** Cor do agrupamento "Outros" — fora da escala categórica de propósito. */
export const categoricalRest = '#4D4947';

export const font = {
  /**
   * Corpo e interface — Inter, por regra do brandbook ("Sora para títulos,
   * Inter para corpo e interface"). Mantém a chave `stack` (não renomeia)
   * porque `packages/ui/src/charts.tsx` importa `font.stack` diretamente em
   * 8 lugares para rótulo de gráfico — trocar só o valor evita tocar esses
   * consumidores.
   */
  stack: '"Inter", "Helvetica Neue", Arial, sans-serif',
  /**
   * Títulos (`h1`/`h2`/`h3`) — Sora, 700-800. Só usado via CSS
   * (`var(--font-heading)` em `global.css`), sem consumidor em JS.
   */
  heading: '"Sora", "Helvetica Neue", Arial, sans-serif',
} as const;

/**
 * Escala tipográfica. Hierarquia se constrói por tamanho e peso — **nunca por
 * cor** (princípio 1 do design system).
 *
 * Os tamanhos de display são menores que os da referência web (48–80px) porque
 * esta é uma superfície mobile. O que se mantém é a proporção e o respiro.
 */
export const type = {
  display: { size: '34px', weight: 600, tracking: '-0.02em', leading: 1.1 },
  h2: { size: '28px', weight: 600, tracking: '-0.015em', leading: 1.15 },
  h3: { size: '22px', weight: 600, tracking: '-0.01em', leading: 1.2 },
  bodyLarge: { size: '19px', weight: 400, tracking: '0', leading: 1.4 },
  body: { size: '17px', weight: 400, tracking: '0', leading: 1.47 },
  caption: { size: '13px', weight: 400, tracking: '0', leading: 1.4 },
  eyebrow: { size: '12px', weight: 600, tracking: '0.06em', leading: 1.2 },
} as const;

/** Escala de espaçamento. Whitespace generoso é o que evita o dashboard
 *  clínico parecer painel de instrumentos (build-prompt §8.4). */
/**
 * `xs`/`sm`/`md`/`lg` já batem exatamente com os 4 primeiros degraus do
 * brandbook (4/8/16/24px) — sem mudança. `xl` vira o 5º degrau do brandbook
 * (40px, era 32px). `xxl`/`section` não têm equivalente no brandbook (que
 * para em 40px); sobem proporcionalmente para continuar a progressão.
 */
export const space = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '40px',
  xxl: '64px',
  section: '96px',
} as const;

export const radius = {
  /** Botões pill — já batia com o brandbook, sem mudança. */
  pill: '980px',
  /** --sb-radius-card do brandbook. */
  card: '10px',
  /** Sem valor próprio no brandbook — alinhado ao card. */
  input: '10px',
} as const;

export const shadow = {
  /** --sb-shadow-card do brandbook — quase imperceptível, nunca sombra dura. */
  card: '0 4px 16px rgba(0, 0, 0, 0.06)',
  /** Sem segundo nível no brandbook — escalado proporcionalmente. */
  raised: '0 8px 28px rgba(0, 0, 0, 0.08)',
} as const;

export interface CssVariableOptions {
  /**
   * Injeta a paleta semântica do semáforo.
   *
   * `false` por padrão, e o app do paciente deve manter assim: ele nunca mostra
   * verde/amarelo/vermelho (build-prompt §3.1). Embarcar as cores num app que
   * não pode usá-las é convite para alguém usá-las depois — o default fecha
   * essa porta em vez de confiar na disciplina de quem vier.
   *
   * O painel do médico liga.
   */
  includeStatusPalette?: boolean;
}

/** Gera as CSS custom properties injetadas no `:root`. */
export function cssVariables({ includeStatusPalette = false }: CssVariableOptions = {}): string {
  const statusPalette = includeStatusPalette
    ? `
    --status-green-fg: ${status.green.fg};
    --status-green-bg: ${status.green.bg};
    --status-yellow-fg: ${status.yellow.fg};
    --status-yellow-bg: ${status.yellow.bg};
    --status-red-fg: ${status.red.fg};
    --status-red-bg: ${status.red.bg};
`
    : '';

  return `
    --color-white: ${color.white};
    --color-surface: ${color.surface};
    --color-border: ${color.border};
    --color-text: ${color.text};
    --color-text-secondary: ${color.textSecondary};
    --color-accent: ${color.accent};
    --color-accent-hover: ${color.accentHover};
${statusPalette}
    --font-stack: ${font.stack};
    --font-heading: ${font.heading};

    --space-xs: ${space.xs};
    --space-sm: ${space.sm};
    --space-md: ${space.md};
    --space-lg: ${space.lg};
    --space-xl: ${space.xl};
    --space-xxl: ${space.xxl};
    --space-section: ${space.section};

    --radius-pill: ${radius.pill};
    --radius-card: ${radius.card};
    --radius-input: ${radius.input};

    --shadow-card: ${shadow.card};
    --shadow-raised: ${shadow.raised};
  `.trim();
}
